import { useState } from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import {
  faCircleExclamation,
  faCircleInfo as faSolidCircleInfo,
} from '@awesome.me/kit-4a6bf60416/icons/classic/solid';
import { usePage } from '@inertiajs/react';

import AddCreditsModal from '@/components/billing/add-credits-modal';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Progress } from '@/components/ui/progress';
import { formatDate } from '@/lib/formatter-utils';
import { cmsBillingPath } from '@/lib/routes';
import { cn, getLocalStorage, setLocalStorage } from '@/lib/utils';
import type { CreditNotice, CreditStatus, SharedData } from '@/types/shared';

const LINK_BUTTON_CLASSES = 'text-xs font-semibold';

type CreditUsageReminderProps = {
  status: (CreditStatus | CreditNotice) | null | undefined;
  presentation?: 'banner' | 'transcript';
  dismissalScope?: string;
};

export function CreditUsageReminder({
  status,
  presentation = 'banner',
  dismissalScope,
}: CreditUsageReminderProps) {
  const [dismissedStatusKey, setDismissedStatusKey] = useState<string | null>(null);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const { accountId, accountActive, site } = usePage<SharedData>().props;
  if (!status?.level) return null;

  const transcript = presentation === 'transcript';
  const graceGranted = 'kind' in status && status.kind === 'grace_granted';
  const grace = status.grace;

  const exhausted = status.level === 'exhausted';
  const critical = status.level === 'critical';
  const graceUrgent = status.graceOnly;
  const criticalAlert = exhausted || graceUrgent;
  const periodEndsAt = 'periodEndsAt' in status ? status.periodEndsAt : null;
  // Site Assistant supplies its thread ID, so a dismissal belongs to that
  // conversation. Other assistants retain their account-level dismissal.
  const statusKey = [
    'credit-usage-reminder',
    accountId ?? 'unknown',
    dismissalScope ?? 'account',
    periodEndsAt ?? 'unbounded',
    status.level,
  ].join(':');
  const dismissed = dismissedStatusKey === statusKey || getLocalStorage(statusKey) === 'dismissed';
  if (!transcript && !graceUrgent && dismissed) return null;

  const notificationPercent = status.threshold ?? status.percentUsed;
  const title = criticalAlert
    ? 'You’ve used all of your AI credits'
    : `You’ve used ${notificationPercent}% of your AI credits`;
  const periodDate = periodEndsAt
    ? formatDate(periodEndsAt, { timeZone: site?.timeZoneIdentifier, includeTime: false })
    : null;
  const description = criticalAlert
    ? graceUrgent && grace
      ? `We’ve given you ${formatNumber(grace.credits)} additional credits before AI actions pause. After that, you can still make manual edits to your site.`
      : periodDate
        ? `Upgrade your plan, purchase additional credits, or wait until your credits reset on ${periodDate}. You can still make manual edits to your site.`
        : 'Upgrade your plan or purchase additional credits to continue using the AI Assistant. You can still make manual edits to your site.'
    : critical
      ? 'Based on your recent activity, your credits may run out before they reset. Upgrade your plan or add credits now to avoid interruptions.'
      : 'Upgrade your plan or add credits now to avoid interruptions.';

  return (
    <Alert
      variant={criticalAlert ? 'destructive' : critical ? 'warning' : 'info'}
      className={cn(
        'border px-4 [&>svg]:self-center [&>svg]:translate-y-0',
        transcript
          ? 'min-w-0 px-3 py-2 has-[>svg]:grid-cols-[calc(var(--spacing)*4)_minmax(0,1fr)] has-[>svg]:gap-x-2'
          : graceUrgent
            ? 'px-3 py-2 has-[>svg]:gap-x-2'
            : 'px-3 py-2.5 has-[>svg]:gap-x-2',
        criticalAlert
          ? 'border-destructive/20 text-foreground [&>svg]:text-destructive [&_[data-slot=alert-description]]:text-foreground'
          : critical
            ? 'border-amber-500/20 text-foreground [&>svg]:text-amber-600'
            : 'border-primary/20 text-foreground [&>svg]:text-primary',
      )}
    >
      <Icon icon={criticalAlert ? faCircleExclamation : faSolidCircleInfo} />
      <AlertTitle
        className={cn(
          transcript ? 'mb-1 min-w-0 text-sm leading-relaxed' : 'pr-8',
          graceUrgent && 'text-sm leading-snug',
        )}
      >
        {title}
      </AlertTitle>
      {!transcript && !criticalAlert ? (
        <Button
          variant="ghost"
          colorScheme="secondary"
          size="icon-sm"
          className="absolute top-2 right-2"
          aria-label="Dismiss credit usage notice"
          onClick={() => {
            setLocalStorage(statusKey, 'dismissed');
            setDismissedStatusKey(statusKey);
          }}
        >
          <Icon icon={faXmark} />
        </Button>
      ) : null}
      <AlertDescription
        className={cn('text-xs', transcript ? 'min-w-0 gap-1' : undefined, graceUrgent && 'gap-2')}
      >
        {!transcript || graceGranted ? (
          <p className={graceUrgent ? 'leading-snug' : undefined}>{description}</p>
        ) : null}
        {!transcript && graceUrgent && grace ? (
          <div className="mt-2 w-full">
            <Progress value={100 - grace.percentUsed} colorScheme="destructive" />
            <div className="mt-1.5 grid grid-cols-[1fr_auto_1fr] text-xs">
              <span>0</span>
              <span>{formatNumber(grace.remaining)} credits remaining</span>
              <span className="text-right">{formatNumber(grace.credits)}</span>
            </div>
          </div>
        ) : null}
        <div
          className={cn(
            'flex items-center whitespace-nowrap',
            transcript ? 'gap-4' : graceUrgent ? 'gap-3 overflow-x-auto' : 'gap-4 overflow-x-auto',
          )}
        >
          {!criticalAlert ? (
            <a
              href={cmsBillingPath({ tab: 'ai-usage' })}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(LINK_BUTTON_CLASSES, 'text-primary hover:underline')}
            >
              View usage
            </a>
          ) : null}
          {accountActive === true ? (
            <Button
              variant="link"
              colorScheme="primary"
              className={LINK_BUTTON_CLASSES}
              onClick={() => setAddCreditsOpen(true)}
            >
              + Add Credits
            </Button>
          ) : null}
        </div>
      </AlertDescription>
      <AddCreditsModal open={addCreditsOpen} onOpenChange={setAddCreditsOpen} />
    </Alert>
  );
}

function formatNumber(value: number) {
  return new Intl.NumberFormat().format(value);
}
